import React from 'react';
import { Users, GraduationCap, Sprout, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Programs = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1558008258-3256797b43f3)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 to-emerald-800/70"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Our Work
          </h1>
          <p className="text-xl text-emerald-50 max-w-2xl mx-auto">
            Supporting under-resourced communities to build sustainable futures
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-gray-600 leading-relaxed">
              Delinam Foundation works directly with grassroots communities—including African diaspora 
              communities and other under-resourced populations—to address barriers to opportunity and 
              create pathways to self-reliance. Our programs are designed with communities, not for them, 
              ensuring solutions are culturally relevant, locally owned, and built to last.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Detail */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20 max-w-6xl mx-auto">
            
            {/* Program 1: Community Development */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-600 rounded-lg mb-6">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Community Development</h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  We invest in grassroots organizations and community-led initiatives that address immediate 
                  needs while building long-term infrastructure for economic and social resilience. Working 
                  closely with local leaders, we support projects that strengthen social cohesion, improve 
                  access to essential services, and create foundations for sustainable growth in under-resourced 
                  neighborhoods.
                </p>
                <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                  <h4 className="font-semibold text-emerald-900 mb-3">Our approach includes:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Grassroots capacity building and organizational support
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Community infrastructure and resource access
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Local partnership development
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Emergency relief and basic needs support
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1559223607-a43c990c692c"
                  alt="Community Development"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Program 2: Youth Empowerment */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-600 rounded-lg mb-6">
                  <GraduationCap className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Youth Empowerment</h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Young people in under-resourced communities face significant barriers to education, 
                  employment, and opportunity. We provide mentorship, skills training, and pathways to 
                  leadership that help youth overcome systemic obstacles and become drivers of change in 
                  their communities. Our programs emphasize practical skills, cultural identity, and 
                  community connection.
                </p>
                <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                  <h4 className="font-semibold text-emerald-900 mb-3">Our approach includes:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Education access and academic support
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Mentorship and life skills development
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Leadership training and civic engagement
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Career readiness and employment pathways
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-xl lg:order-1">
                <img
                  src="https://images.unsplash.com/photo-1559315558-6fbcbd3cf22d"
                  alt="Youth Empowerment"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Program 3: Sustainable Livelihoods */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-600 rounded-lg mb-6">
                  <Sprout className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Sustainable Livelihoods</h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Economic security is fundamental to community stability. We support individuals and families 
                  in building sustainable income through practical skills training, small business development, 
                  and access to employment opportunities. Our programs recognize the entrepreneurial spirit 
                  present in under-resourced communities and provide the tools, connections, and support to 
                  turn ideas into sustainable enterprises.
                </p>
                <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                  <h4 className="font-semibold text-emerald-900 mb-3">Our approach includes:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Vocational and technical skills training
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Microenterprise and small business support
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Financial literacy and resource management
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Job placement and workforce development
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1492496913980-501348b61469"
                  alt="Sustainable Livelihoods"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Program 4: Local Leadership & Governance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-600 rounded-lg mb-6">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Leadership Development</h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Sustainable change requires strong, ethical local leadership. We identify and support 
                  emerging community leaders—including women, youth, and voices historically excluded from 
                  decision-making spaces. Through training, coaching, and network building, we help leaders 
                  develop the skills and confidence to advocate for their communities, manage resources 
                  effectively, and create lasting institutional change.
                </p>
                <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                  <h4 className="font-semibold text-emerald-900 mb-3">Our approach includes:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Leadership training and capacity building
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Community organizing and advocacy
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Network building and peer learning
                    </li>
                    <li className="flex items-start">
                      <span className="text-emerald-600 mr-2">•</span>
                      Organizational development and governance
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-xl lg:order-1">
                <img
                  src="https://images.unsplash.com/photo-1544806861-dc05b18f6c13"
                  alt="Local Leadership"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why This Work Matters</h2>
            <Card className="border-2 border-emerald-200">
              <CardContent className="p-10">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p className="text-lg">
                    Under-resourced communities—including African diaspora communities, immigrant populations, 
                    and economically marginalized neighborhoods—face systemic barriers that limit access to 
                    opportunity. These barriers are not due to lack of talent, ambition, or work ethic. They 
                    are the result of historical inequity, disinvestment, and structural disadvantage.
                  </p>
                  <p className="text-lg">
                    Delinam Foundation works to dismantle these barriers by investing in the leadership, 
                    capacity, and self-determination of communities themselves. We believe sustainable change 
                    happens when communities have agency over their own development—when solutions are designed 
                    with them, not for them.
                  </p>
                  <p className="text-lg font-medium text-emerald-800">
                    Every program we support is grounded in partnership, cultural respect, and a commitment 
                    to measurable, long-term impact.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to Learn More or Get Involved?
          </h2>
          <p className="text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Connect with us to discover how you can support these transformative programs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-involved">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8">
                Get Involved
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
